/**
 * Signature helpers.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * Why this file exists at all, and why it does not simply call Graph:
 *
 * Microsoft Graph does not expose the Outlook signature. Not in v1.0, not in beta. The obvious
 * candidate, GET /me/mailboxSettings, returns archiveFolder, automaticRepliesSetting, dateFormat,
 * delegateMeetingMessageDeliveryOptions, language, timeFormat, timeZone, userPurpose and
 * workingHours — and nothing else. Roaming signatures live in the mailbox as a hidden
 * folder-associated (FAI) user-configuration item under the message root, reachable only through
 * EWS GetUserConfiguration or the Outlook add-in JS API. Neither is callable from a browser SPA
 * holding delegated Graph tokens.
 *
 * So the panel does not claim to read anyone's signature. It offers candidates and labels where
 * each one came from. Anyone tempted to "fix" this by reaching for /me/signature or
 * /me/outlook/signature should know those endpoints do not exist and will 404.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 */

import { IOutlookProfile } from '../models/IOutlookProfile.interface';

/** Escapes text destined for an HTML attribute or text node. */
function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Builds a signature block from directory fields.
 *
 * This is the primary candidate because it is the only one that always works: it needs nothing
 * beyond `user.read`, which the base sign-in already consents, and it cannot be wrong in the way a
 * heuristic can — every line is a field the directory reported. Empty fields are dropped rather
 * than rendered blank, which is what makes it safe on sparse records and on personal accounts,
 * where jobTitle/department/companyName simply do not exist.
 *
 * Inline styles rather than classes: the output is meant to survive a copy-paste into the OWA
 * signature editor, which strips stylesheets but keeps inline style attributes.
 */
export function buildDerivedSignatureHtml(profile: IOutlookProfile): string {
  const font = 'font-family: Lato, Segoe UI, Arial, sans-serif;';
  const muted = 'color:#5a6472;';

  const lines: string[] = [];

  lines.push(
    `<div style="${font} font-size:14px; font-weight:600; color:#1f2933;">${esc(profile.displayName)}</div>`
  );

  // Title and department read naturally on one line when both are present.
  const role = [profile.jobTitle, profile.department].filter(Boolean).join(' · ');
  if (role) {
    lines.push(`<div style="${font} font-size:13px; ${muted}">${esc(role)}</div>`);
  }

  if (profile.companyName) {
    lines.push(
      `<div style="${font} font-size:13px; color:#1f2933; font-weight:500;">${esc(profile.companyName)}</div>`
    );
  }

  const contactBits: string[] = [];
  const email = profile.mail || profile.userPrincipalName;
  if (email) {
    contactBits.push(`<a href="mailto:${esc(email)}" style="color:#059669; text-decoration:none;">${esc(email)}</a>`);
  }
  if (profile.mobilePhone) {
    contactBits.push(`M ${esc(profile.mobilePhone)}`);
  }
  if (profile.businessPhones && profile.businessPhones.length) {
    contactBits.push(`T ${esc(profile.businessPhones[0])}`);
  }
  if (contactBits.length) {
    lines.push(
      `<div style="${font} font-size:13px; ${muted} margin-top:6px;">${contactBits.join(' &nbsp;|&nbsp; ')}</div>`
    );
  }

  const place = [profile.officeLocation, profile.streetAddress, profile.city, profile.postalCode, profile.country]
    .filter(Boolean)
    .join(', ');
  if (place) {
    lines.push(`<div style="${font} font-size:12px; ${muted}">${esc(place)}</div>`);
  }

  return `<div style="${font} line-height:1.45;">\n  ${lines.join('\n  ')}\n</div>`;
}

/** Plain-text flattening of a signature, for the text/plain clipboard flavour. */
export function signatureHtmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Block-level boundaries become newlines; without this everything collapses onto one line.
  doc.querySelectorAll('div, p, br, tr').forEach((el) => el.insertAdjacentText('beforebegin', '\n'));
  return (doc.body.textContent || '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .join('\n');
}

export interface ExtractedSignature {
  html: string;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Best-effort recovery of a signature from a sent message body.
 *
 * Four strategies, tried in descending order of how much they can be trusted, and returning null
 * rather than guessing when none of them fire. A wrong signature silently pasted into someone's
 * correspondence is worse than no candidate at all, so there is deliberately no "take the last
 * paragraph and hope" fallback.
 */
export function extractSignatureFromHtml(bodyHtml: string): ExtractedSignature | null {
  if (!bodyHtml || !bodyHtml.trim()) {
    return null;
  }

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(bodyHtml, 'text/html');
  } catch {
    return null;
  }

  // 1. The explicit wrappers OWA and Outlook desktop emit around a signature. When one of these is
  //    present it is not a guess — the client told us exactly which block it is.
  const marked = doc.querySelector('#Signature, [id^="Signature"], .ms-outlook-signature, [class*="signature"]');
  if (marked && (marked.textContent || '').trim().length > 0) {
    return { html: marked.innerHTML.trim(), confidence: 'high' };
  }

  // 2. The RFC 3676 delimiter — a line containing exactly "-- ". Long-standing convention, and
  //    still what most non-Outlook clients emit.
  const html = doc.body.innerHTML;
  const delimiter = /(?:^|<br\s*\/?>|<\/div>|<\/p>)\s*--\s*(?:<br\s*\/?>|\n)/i;
  const delimiterMatch = delimiter.exec(html);
  if (delimiterMatch) {
    const tail = html.slice(delimiterMatch.index + delimiterMatch[0].length).trim();
    if (tail && tail.length < 4000) {
      return { html: tail, confidence: 'medium' };
    }
  }

  // 3. A trailing block after the last horizontal rule, but only when it is short enough to
  //    plausibly be a signature rather than a quoted thread.
  const rules = doc.querySelectorAll('hr');
  if (rules.length) {
    const last = rules[rules.length - 1];
    const parts: string[] = [];
    let node: ChildNode | null = last.nextSibling;
    while (node) {
      parts.push(node instanceof Element ? node.outerHTML : (node.textContent || ''));
      node = node.nextSibling;
    }
    const tail = parts.join('').trim();
    if (tail && tail.length < 1500 && (last.parentElement?.textContent || '').trim().length > 0) {
      return { html: tail, confidence: 'low' };
    }
  }

  // 4. Nothing recognisable. Say so.
  return null;
}
